import type { PropsWithChildren } from "react";
import { useEffect } from "react";

import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

enum FadeInUpDuration {
  Default = 1150,
}

enum FadeInUpDelay {
  Default = 500,
  Fast = 150,
  None = 0,
}

enum FadeInUpDistance {
  Default = 28,
}

const FadeInUpEasing = Easing.bezier(0.22, 0.9, 0.3, 1);

interface FadeInUpViewProps extends PropsWithChildren {
  delay?: number;
}

/** Fades in while translating up 28px. Duration 1150ms with a deceleration curve. */
const FadeInUpView = ({ children, delay = 0 }: FadeInUpViewProps) => {
  const isReducedMotion = useReducedMotion();
  const opacity = useSharedValue(isReducedMotion ? 1 : 0);
  const translateY = useSharedValue(
    isReducedMotion ? 0 : FadeInUpDistance.Default,
  );

  useEffect(() => {
    if (isReducedMotion) return;

    opacity.value = withDelay(
      delay,
      withTiming(1, {
        duration: FadeInUpDuration.Default,
        easing: FadeInUpEasing,
      }),
    );
    translateY.value = withDelay(
      delay,
      withTiming(0, {
        duration: FadeInUpDuration.Default,
        easing: FadeInUpEasing,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Animation runs once on mount with initial delay value
  }, []);

  const style = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  return <Animated.View style={style}>{children}</Animated.View>;
};

const FadeInUpDelayMap: Record<keyof typeof FadeInUpDelay, FadeInUpDelay> = {
  Default: FadeInUpDelay.Default,
  Fast: FadeInUpDelay.Fast,
  None: FadeInUpDelay.None,
};

const FadeInUpDelayValues = Object.values(FadeInUpDelayMap);

export type { FadeInUpViewProps };
export { FadeInUpDelay, FadeInUpDelayValues, FadeInUpView };
